import React, { useEffect, useMemo, useState } from "react";
import { api, urls } from "../../api/api";
import {
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
} from "reactstrap";

import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";

const getCurrentUser = () => {
  try {
    return JSON.parse(localStorage.getItem("user") || "{}");
  } catch {
    return {};
  }
};
const isAdmin = () => (getCurrentUser()?.role || "") === "admin";
const isAgent = () => (getCurrentUser()?.role || "") === "agent";
const currentUserId = () => Number(getCurrentUser()?.id || 0);

const STATUS_OPTIONS = [
  { value: "en_attente", label: "En attente", color: "#FFE8A3" },
  { value: "confirme", label: "Confirmé", color: "#B9F6CA" },
  { value: "annule", label: "Annulé", color: "#FFC9C9" },
  { value: "reporte", label: "Reporté", color: "#B3E5FF" },
  { value: "realise", label: "Réalisé", color: "#D6CCFF" },
  { value: "no_show", label: "No-show", color: "#E6E6E6" },
];

const statusColor = (st) => STATUS_OPTIONS.find((x) => x.value === st)?.color || "#E6E6E6";

function addMinutes(date, minutes) {
  return new Date(new Date(date).getTime() + minutes * 60000);
}

function getRoundedNow() {
  const d = new Date();
  d.setSeconds(0);
  d.setMilliseconds(0);

  const hour = d.getHours();
  if (hour >= 18 || hour < 8) {
    if (hour >= 18) d.setDate(d.getDate() + 1);
    d.setHours(9, 0, 0, 0);
    return d;
  }

  const m = d.getMinutes();
  if (m === 0 || m === 30) return d;

  if (m < 30) {
    d.setMinutes(30);
    return d;
  }

  d.setHours(d.getHours() + 1);
  d.setMinutes(0);

  if (d.getHours() >= 18) d.setHours(17, 30, 0, 0);
  return d;
}

const addByFreq = (date, freq, interval) => {
  const d = new Date(date);
  if (freq === "monthly") d.setMonth(d.getMonth() + interval);
  else d.setDate(d.getDate() + 7 * interval);
  return d;
};

const normalizeEvent = (x) => {
  const id = x.id ?? x.rdvId ?? x.uuid;
  const start = x.start ?? x.start_at ?? x.startAt ?? x.start_time;
  const end = x.end ?? x.end_at ?? x.endAt ?? x.end_time;

  const ep = x.extendedProps || x.extended_props || {};

  const clientId = ep.clientId ?? ep.client_id ?? x.client_id ?? x.clientId ?? "";
  const serviceId = ep.serviceId ?? ep.service_id ?? x.service_id ?? x.serviceId ?? "";
  const agentId = ep.agentId ?? ep.agent_id ?? x.agent_id ?? x.agentId ?? "";

  const status = ep.status ?? x.status ?? "en_attente";

  const clientObj = ep.client ?? x.client ?? null;
  const serviceObj = ep.service ?? x.service ?? null;
  const agentObj = ep.agent ?? x.agent ?? null;

  return {
    id,
    title: x.title || ep.title || (clientObj?.full_name ? clientObj.full_name : "RDV"),
    start,
    end,
    extendedProps: {
      ...ep,
      status,
      clientId,
      serviceId,
      agentId,
      clientName: ep.clientName ?? x.clientName ?? clientObj?.full_name ?? "",
      serviceName: ep.serviceName ?? x.serviceName ?? serviceObj?.name ?? "",
      agentName: ep.agentName ?? x.agentName ?? agentObj?.name ?? "",
      phone: ep.phone ?? x.phone ?? clientObj?.phone ?? "",
      notes: ep.notes ?? x.notes ?? ep.note ?? x.note ?? "",
      motifAnnulation:
        ep.motifAnnulation ?? ep.motif_annulation ?? x.motif_annulation ?? x.motifAnnulation ?? "",
    },
  };
};

const Reservations = () => {
  document.title = "Réservations";

  const admin = isAdmin();
  const agent = isAgent();
  const meId = currentUserId();

  const [filterStatus, setFilterStatus] = useState("");
  const [filterService, setFilterService] = useState("");
  const [filterAgent, setFilterAgent] = useState("");

  const [events, setEvents] = useState([]);
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);

  // ✅ agents list only for admin (for filter + select)
  const [agents, setAgents] = useState([]);

  const [loadingClients, setLoadingClients] = useState(false);
  const [loadingServices, setLoadingServices] = useState(false);
  const [loadingAgents, setLoadingAgents] = useState(false);

  const [editingId, setEditingId] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);

  const [form, setForm] = useState({
    clientId: "",
    serviceId: "",
    agentId: agent ? String(meId) : "",
    duration: 30,
    status: "en_attente",
    notes: "",
    motifAnnulation: "",

    isRecurring: false,
    recurrenceFreq: "weekly",
    recurrenceInterval: 1,
    recurrenceCount: 4,

    _clientName: "",
    _phone: "",
    _serviceName: "",
    _agentName: "",
  });

  const toggle = () => {
    setIsOpen((v) => !v);
    if (isOpen) setEditingId(null);
  };

  const resetForm = () => {
    setForm({
      clientId: "",
      serviceId: "",
      agentId: agent ? String(meId) : "",
      duration: 30,
      status: "en_attente",
      notes: "",
      motifAnnulation: "",
      isRecurring: false,
      recurrenceFreq: "weekly",
      recurrenceInterval: 1,
      recurrenceCount: 4,
      _clientName: "",
      _phone: "",
      _serviceName: "",
      _agentName: "",
    });
  };

  const filteredEvents = useMemo(() => {
    return (events || []).filter((e) => {
      const st = e.extendedProps?.status || "";
      const srv = e.extendedProps?.serviceId || "";
      const ag = e.extendedProps?.agentId || "";

      if (filterStatus && st !== filterStatus) return false;
      if (filterService && String(srv) !== String(filterService)) return false;
      if (admin && filterAgent && String(ag) !== String(filterAgent)) return false;

      // ✅ agent always sees his data only from backend already, so no extra filter needed
      return true;
    });
  }, [events, filterStatus, filterService, filterAgent, admin]);

  const refreshEvents = async () => {
    const r = await api.get(urls.rdvs);
    const arr = Array.isArray(r.data) ? r.data : [];
    setEvents(arr.map(normalizeEvent));
  };

  const refreshClients = async () => {
    setLoadingClients(true);
    try {
      const r = await api.get(urls.clients);
      setClients(Array.isArray(r.data) ? r.data : []);
    } finally {
      setLoadingClients(false);
    }
  };

  const refreshServices = async () => {
    setLoadingServices(true);
    try {
      const r = await api.get(urls.services);
      setServices(Array.isArray(r.data) ? r.data : []);
    } finally {
      setLoadingServices(false);
    }
  };

  const refreshAgents = async () => {
    if (!admin) return; // ✅ agent ماكيحتاجش هذا endpoint
    setLoadingAgents(true);
    try {
      const r = await api.get(urls.agents);
      setAgents(Array.isArray(r.data) ? r.data : []);
    } finally {
      setLoadingAgents(false);
    }
  };

  useEffect(() => {
    Promise.all([refreshEvents(), refreshClients(), refreshServices(), refreshAgents()]).catch((e) => {
      alert(e?.response?.data?.message || "Erreur chargement (backend/token?)");
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((p) => ({
      ...p,
      [name]:
        type === "checkbox"
          ? checked
          : name === "duration" || name === "recurrenceInterval" || name === "recurrenceCount"
          ? parseInt(value, 10)
          : value,
    }));
  };

  const handleServiceChange = (e) => {
    const serviceId = e.target.value;
    const s = services.find((x) => String(x.id) === String(serviceId));
    setForm((p) => ({
      ...p,
      serviceId,
      duration: s ? Number(s.duration_min || s.durationMin || 30) : p.duration,
    }));
  };

  const handleDateSelect = (selectInfo) => {
    setEditingId(null);
    setSelectedSlot({ start: selectInfo.start, end: selectInfo.end });
    resetForm();
    setIsOpen(true);
  };

  const handleEventClick = (clickInfo) => {
    const ev = clickInfo.event;
    const endDate = ev.end ? ev.end : addMinutes(ev.start, 30);

    setEditingId(ev.id);
    setSelectedSlot({ start: ev.start, end: endDate });

    const found = events.find((x) => String(x.id) === String(ev.id));
    const ep = found?.extendedProps || ev.extendedProps || {};

    const durationMin = Math.round((endDate.getTime() - ev.start.getTime()) / 60000);

    const c = clients.find((x) => String(x.id) === String(ep.clientId));
    const s = services.find((x) => String(x.id) === String(ep.serviceId));

    const a = admin ? agents.find((x) => String(x.id) === String(ep.agentId)) : null;

    const clientName = c?.full_name || c?.fullName || ep.clientName || "";
    const phone = c?.phone || ep.phone || "";
    const serviceName = s?.name || ep.serviceName || "";
    const agentName = admin ? (a?.name || a?.fullName || ep.agentName || "") : (getCurrentUser()?.name || getCurrentUser()?.email || "");

    setForm((p) => ({
      ...p,
      clientId: ep.clientId || "",
      serviceId: ep.serviceId || "",
      agentId: agent ? String(meId) : (ep.agentId || ""),
      duration: durationMin || 30,
      status: ep.status || "en_attente",
      notes: ep.notes || "",
      motifAnnulation: ep.motifAnnulation || "",
      isRecurring: false,
      _clientName: clientName,
      _phone: phone,
      _serviceName: serviceName,
      _agentName: agentName,
    }));

    setIsOpen(true);
  };

  const updateRdvTime = async (info) => {
    const ev = info.event;
    try {
      await api.put(`${urls.rdvs}/${ev.id}`, {
        start: ev.start.toISOString(),
        end: ev.end ? ev.end.toISOString() : addMinutes(ev.start, 30).toISOString(),
      });
      await refreshEvents();
    } catch (e) {
      alert(e?.response?.data?.message || "Erreur update");
      info.revert();
    }
  };

  const handleSave = async () => {
    if (!selectedSlot?.start) return alert("Start required");
    if (!form.clientId) return alert("Veuillez choisir un client");
    if (!form.serviceId) return alert("Veuillez choisir un service");

    // ✅ agentId required only for admin (agent fixed)
    if (admin && !form.agentId) return alert("Veuillez choisir un agent");

    if (form.status === "annule" && !String(form.motifAnnulation || "").trim()) {
      return alert("Motif d'annulation obligatoire");
    }

    const start = new Date(selectedSlot.start);
    const end = addMinutes(start, Number(form.duration || 30));

    const basePayload = {
      client_id: Number(form.clientId),
      service_id: Number(form.serviceId),
      agent_id: admin ? Number(form.agentId) : meId, // ✅ agent forced
      status: form.status,
      notes: form.notes,
      motif_annulation: form.motifAnnulation,
    };

    try {
      // recurring create
      if (!editingId && form.isRecurring) {
        try {
          await api.post(urls.rdvsRecurring, {
            ...basePayload,
            start: start.toISOString(),
            duration_min: Number(form.duration || 30),
            recurrence_freq: form.recurrenceFreq,
            recurrence_interval: Number(form.recurrenceInterval || 1),
            recurrence_count: Number(form.recurrenceCount || 4),
          });
        } catch (_) {
          // fallback: create N normal rdvs
          const count = Number(form.recurrenceCount || 4);
          const interval = Number(form.recurrenceInterval || 1);
          let cur = new Date(start);

          for (let i = 0; i < count; i++) {
            const s = new Date(cur);
            const en = addMinutes(s, Number(form.duration || 30));
            await api.post(urls.rdvs, { ...basePayload, start: s.toISOString(), end: en.toISOString() });
            cur = addByFreq(cur, form.recurrenceFreq, interval);
          }
        }

        await refreshEvents();
        setIsOpen(false);
        setEditingId(null);
        return;
      }

      if (editingId) {
        await api.put(`${urls.rdvs}/${editingId}`, { ...basePayload, start: start.toISOString(), end: end.toISOString() });
      } else {
        await api.post(urls.rdvs, { ...basePayload, start: start.toISOString(), end: end.toISOString() });
      }

      await refreshEvents();
      setIsOpen(false);
      setEditingId(null);
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        (e?.response?.data?.errors ? Object.values(e.response.data.errors).flat().join("\n") : null) ||
        "Erreur backend";
      alert(msg);
    }
  };

  const handleDelete = async () => {
    if (!editingId) return;
    if (!window.confirm("Supprimer ce RDV ?")) return;

    try {
      await api.delete(`${urls.rdvs}/${editingId}`);
      setIsOpen(false);
      setEditingId(null);
      await refreshEvents();
    } catch (e) {
      alert(e?.response?.data?.message || "Erreur suppression");
    }
  };

  return (
    <React.Fragment>
      {/* Filters */}
      <Row className="mb-2">
        <Col md={admin ? 4 : 6}>
          <Input type="select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="">-- Tous statuts --</option>
            {STATUS_OPTIONS.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </Input>
        </Col>

        <Col md={admin ? 4 : 6}>
          <Input type="select" value={filterService} onChange={(e) => setFilterService(e.target.value)}>
            <option value="">-- Tous services --</option>
            {services.map((s) => (
              <option key={s.id} value={String(s.id)}>{s.name}</option>
            ))}
          </Input>
        </Col>

        {admin && (
          <Col md={4}>
            <Input type="select" value={filterAgent} onChange={(e) => setFilterAgent(e.target.value)} disabled={loadingAgents}>
              <option value="">-- Tous agents --</option>
              {agents.map((a) => (
                <option key={a.id} value={String(a.id)}>{a.name}</option>
              ))}
            </Input>
          </Col>
        )}
      </Row>

      <Card className="mt-2">
        <CardBody className="pt-2">
          <div className="d-flex justify-content-end mb-2">
            <Button
              color="primary"
              onClick={() => {
                setEditingId(null);
                const s = getRoundedNow();
                setSelectedSlot({ start: s, end: addMinutes(s, 30) });
                resetForm();
                setIsOpen(true);
              }}
            >
              + Ajouter RDV
            </Button>
          </div>

          <FullCalendar
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
            initialView="timeGridWeek"
            timeZone="local"
            headerToolbar={{
              left: "prev,next today",
              center: "title",
              right: "dayGridMonth,timeGridWeek,timeGridDay",
            }}
            selectable
            selectMirror
            editable
            events={filteredEvents}
            select={handleDateSelect}
            eventClick={handleEventClick}
            eventDrop={updateRdvTime}
            eventResize={updateRdvTime}
            height={650}
            nowIndicator
            eventDidMount={(info) => {
              const st = info.event.extendedProps?.status || "en_attente";
              const color = statusColor(st);
              info.el.style.backgroundColor = color;
              info.el.style.borderColor = color;
              info.el.style.color = "#111";
            }}
          />
        </CardBody>
      </Card>

      {/* Modal */}
      <Modal isOpen={isOpen} toggle={toggle} centered>
        <ModalHeader toggle={toggle}>{editingId ? "Modifier RDV" : "Ajouter un RDV"}</ModalHeader>

        <ModalBody>
          <Form>
            <Row>
              {editingId && (
                <Col md={12} className="mb-2">
                  <div className="p-2 rounded" style={{ background: "#f8f9fa", fontSize: 13 }}>
                    <div><b>Client:</b> {form._clientName || "-"}</div>
                    <div><b>Tél:</b> {form._phone || "-"}</div>
                    <div><b>Service:</b> {form._serviceName || "-"}</div>
                    <div><b>Agent:</b> {form._agentName || "-"}</div>
                  </div>
                </Col>
              )}

              <Col md={12}>
                <FormGroup>
                  <Label>Client</Label>
                  <Input
                    type="select"
                    value={form.clientId}
                    onChange={(e) => setForm((p) => ({ ...p, clientId: e.target.value }))}
                    disabled={loadingClients}
                  >
                    <option value="">{loadingClients ? "Chargement..." : "-- Choisir un client --"}</option>
                    {clients.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.full_name} {c.phone ? `(${c.phone})` : ""}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>

              <Col md={12}>
                <FormGroup>
                  <Label>Service</Label>
                  <Input type="select" value={form.serviceId} onChange={handleServiceChange} disabled={loadingServices}>
                    <option value="">{loadingServices ? "Chargement..." : "-- Choisir service --"}</option>
                    {services.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name} ({s.duration_min || s.durationMin || 30} min)
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>

              {/* ✅ Agent: مايبانش select ديال agent */}
              {admin ? (
                <Col md={12}>
                  <FormGroup>
                    <Label>Agent</Label>
                    <Input
                      type="select"
                      value={form.agentId ?? ""}
                      onChange={(e) => setForm((p) => ({ ...p, agentId: e.target.value }))}
                      disabled={loadingAgents}
                    >
                      <option value="">{loadingAgents ? "Chargement..." : "-- Choisir un agent --"}</option>
                      {agents.map((a) => (
                        <option key={a.id} value={String(a.id)}>{a.name}</option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>
              ) : (
                <Col md={12} className="mb-2">
                  <div className="text-muted" style={{ fontSize: 12 }}>
                    Agent: <b>{getCurrentUser()?.name || getCurrentUser()?.email || "—"}</b>
                  </div>
                </Col>
              )}

              {!editingId && (
                <Col md={12}>
                  <FormGroup check className="mb-2">
                    <Input type="checkbox" name="isRecurring" checked={form.isRecurring} onChange={onChange} />
                    <Label check className="ms-2">RDV Récurrent</Label>
                  </FormGroup>
                </Col>
              )}

              {form.isRecurring && !editingId && (
                <>
                  <Col md={4}>
                    <FormGroup>
                      <Label>Fréquence</Label>
                      <Input type="select" name="recurrenceFreq" value={form.recurrenceFreq} onChange={onChange}>
                        <option value="weekly">Weekly</option>
                        <option value="monthly">Monthly</option>
                      </Input>
                    </FormGroup>
                  </Col>

                  <Col md={4}>
                    <FormGroup>
                      <Label>Interval</Label>
                      <Input type="number" name="recurrenceInterval" min={1} value={form.recurrenceInterval} onChange={onChange} />
                    </FormGroup>
                  </Col>

                  <Col md={4}>
                    <FormGroup>
                      <Label>Nombre (Count)</Label>
                      <Input type="number" name="recurrenceCount" min={2} value={form.recurrenceCount} onChange={onChange} />
                    </FormGroup>
                  </Col>
                </>
              )}

              <Col md={6}>
                <FormGroup>
                  <Label>Durée (minutes)</Label>
                  <Input type="number" value={form.duration} readOnly />
                  <div className="text-muted" style={{ fontSize: 12 }}>* Par défaut: service</div>
                </FormGroup>
              </Col>

              <Col md={6}>
                <FormGroup>
                  <Label>Statut</Label>
                  <Input type="select" name="status" value={form.status} onChange={onChange}>
                    {STATUS_OPTIONS.map((s) => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>

              {form.status === "annule" && (
                <Col md={12}>
                  <FormGroup>
                    <Label>Motif d'annulation</Label>
                    <Input
                      name="motifAnnulation"
                      value={form.motifAnnulation}
                      onChange={onChange}
                      placeholder="Ex: Client indisponible..."
                    />
                  </FormGroup>
                </Col>
              )}

              <Col md={12}>
                <FormGroup>
                  <Label>Notes</Label>
                  <Input
                    type="textarea"
                    rows="3"
                    name="notes"
                    value={form.notes}
                    onChange={onChange}
                    placeholder="Remarques..."
                  />
                </FormGroup>
              </Col>

              <Col md={12}>
                <div className="text-muted" style={{ fontSize: 12 }}>
                  {selectedSlot?.start ? `Créneau: ${new Date(selectedSlot.start).toLocaleString()}` : ""}
                </div>
              </Col>
            </Row>
          </Form>
        </ModalBody>

        <ModalFooter>
          {editingId && (
            <Button color="danger" onClick={handleDelete}>
              Supprimer
            </Button>
          )}
          <Button color="secondary" onClick={toggle}>
            Fermer
          </Button>
          <Button color="primary" onClick={handleSave}>
            Enregistrer
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default Reservations;
